import { useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { resourcesAtom } from 'atoms/root';
import {
  CompletionConfigurationResponse,
  CompletionPromptConfigurationOption,
  DocumentResponse,
} from 'types/api-responses';
import { fileAtom, submitDocumentAtom } from './atoms';
import { getFormDefaults, getDefaultPrompt } from './utils';

interface UseDocumentUploadArgs {
  configurationObject: CompletionConfigurationResponse;
  documentType: string;
  filteredPrompts: CompletionPromptConfigurationOption[];
  onSuccess: (data: DocumentResponse) => void;
  onError?: (error?: unknown) => void;
}

// Shared upload state + submit/file handlers for the document-upload surfaces:
// OCR/LLM/prompt selections, the in-flight guard, and the POST to
// documentUploads. Shell concerns (open state, toasts, picker) stay with caller.
export const useDocumentUpload = ({
  configurationObject,
  documentType,
  filteredPrompts,
  onSuccess,
  onError,
}: UseDocumentUploadArgs) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  // fileAtom is a module-level singleton, so it assumes a single upload surface
  // per page. Mounting two at once would share (and clobber) this file state.
  const [upload, setUpload] = useAtom(fileAtom);
  const formDefaults = getFormDefaults(configurationObject);
  const [ocrModel, setOcrModel] = useState(
    formDefaults.extractionConfigurations
  );
  const [llmModel, setLlmModel] = useState(
    formDefaults.completionConfigurations
  );
  const [promptVersion, setPromptVersion] = useState(() =>
    getDefaultPrompt(filteredPrompts)
  );
  const [, submitDocument] = useAtom(submitDocumentAtom);
  const resources = useAtomValue(resourcesAtom);

  const handleFileUpload = (
    _event: React.SyntheticEvent<HTMLElement>,
    { addedFiles }: { addedFiles: File[] }
  ) => {
    const uploadedFile = addedFiles?.[0];
    if (!uploadedFile) return;
    setUpload(uploadedFile);
  };

  const handleSubmit = () => {
    if (
      upload.status !== 'complete' ||
      !upload.file ||
      !promptVersion ||
      !resources?.documentUploads?.href ||
      isSubmittingRef.current
    ) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    submitDocument({
      type: 'POST',
      url: resources.documentUploads.href,
      data: {
        documentType,
        documentExtraction: { configurationId: ocrModel.id },
        documentExtractionCompletion: {
          configurationId: llmModel.id,
          promptId: promptVersion.id,
        },
        fileSignedId: upload.file,
      },
      onSuccess,
      onError: (error) => {
        console.error('Error submitting document:', error);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        onError?.(error);
      },
    });
  };

  const reset = () => {
    setUpload(undefined);
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  const isUploading = upload.status === 'uploading';
  const isUploadComplete = upload.status === 'complete';
  const submitDisabled =
    !isUploadComplete || !promptVersion || isSubmitting || isUploading;

  return {
    upload,
    setUpload,
    ocrModel,
    setOcrModel,
    llmModel,
    setLlmModel,
    promptVersion,
    setPromptVersion,
    isSubmitting,
    isUploading,
    isUploadComplete,
    submitDisabled,
    handleFileUpload,
    handleSubmit,
    reset,
  };
};
